import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertCircle, CheckCircle, Clock, RotateCcw, 
  XCircle, PlayCircle 
} from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import ConfidenceSlider from '../../../components/shared/ConfidenceSlider';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';

export default function AiAuditGame({ gameData, onComplete }) {
  const { playError, playWin, playExtinguish, playSuccess } = useGameSounds();

  const { 
    reportText = [], 
    totalErrors = 0, 
    id,
    preGameBriefing,
    failureLogic
  } = gameData || {};

  // --- STATE ---
  const [stage, setStage] = useState('briefing'); 
  const [confidencePre, setConfidencePre] = useState(5);
  const [confidencePost, setConfidencePost] = useState(5);
  
  const [startTime, setStartTime] = useState(null);
  const [firstReactionTime, setFirstReactionTime] = useState(null);
  const [sycophancyCaught, setSycophancyCaught] = useState(false);

  // Game Logic
  const [shuffledReport, setShuffledReport] = useState([]);
  const [foundErrors, setFoundErrors] = useState([]);
  const [strikes, setStrikes] = useState(0);
  const [maxStrikes, setMaxStrikes] = useState(3);
  const [timeLeft, setTimeLeft] = useState(0);

  const [activeFeedback, setActiveFeedback] = useState(null);
  const [activeIntervention, setActiveIntervention] = useState(null);

  // --- 1. DIFFICULTY CALCULATOR ---
  const calculateDifficulty = (conf) => {
      // Expert: 30s | 1 Strike (Sudden Death)
      if (conf >= 8) return { time: 30, strikes: 1, label: "EXPERT (Sudden Death)" };
      // Standard: 45s | 3 Strikes
      if (conf >= 5) return { time: 45, strikes: 3, label: "STANDARD (3 Strikes)" };
      // Rookie: 60s | 5 Strikes
      return { time: 60, strikes: 5, label: "TRAINING (5 Strikes)" };
  };

  // --- 2. INITIALIZATION ---
  useEffect(() => {
    if (preGameBriefing) {
        setStage('briefing');
    } else {
        setStage('pre');
    }
    // Shuffle report text once on mount
    const shuffled = [...reportText].sort(() => Math.random() - 0.5);
    setShuffledReport(shuffled);
  }, [preGameBriefing, reportText]);

  // --- 3. START GAME HANDLER ---
  const handleStartGame = () => {
      const settings = calculateDifficulty(confidencePre);
      setTimeLeft(settings.time);
      setMaxStrikes(settings.strikes);
      setStrikes(0);
      setFoundErrors([]);
      setSycophancyCaught(false);
      setStartTime(Date.now());
      setStage('playing');
  };

  // --- 4. TIMER ---
  useEffect(() => {
    if (stage !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          triggerFailure('timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [stage]);

  // --- 5. AUTO-DISMISS FEEDBACK ---
  useEffect(() => {
    if (activeFeedback) {
      const timer = setTimeout(() => {
        setActiveFeedback(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [activeFeedback]);

  if (!gameData) return <div className="p-10 text-center text-red-500">Error: Game Data Missing</div>;

  // --- 6. FAILURE LOGIC ROUTER ---
  const triggerFailure = (reasonOverride = null) => {
      setStage('intervention');
      playExtinguish();

      let reasonCode = reasonOverride || 'timeout';

      // If timeout, verify WHAT was missed
      if (reasonCode === 'timeout') {
          const missedSycophancy = !foundErrors.includes('sycophancy_trap');
          const missedPII = !foundErrors.includes('pii_leak');

          if (missedSycophancy) reasonCode = 'missed_sycophancy';
          else if (missedPII) reasonCode = 'missed_pii';
      }

      const logic = failureLogic?.[reasonCode] || {
          title: "Verification Failed",
          script: "Time's up. You missed critical errors.",
          videoUrl: null
      };

      setActiveIntervention(logic);
  };

  const handleRetry = () => {
      setFoundErrors([]);
      setStrikes(0);
      setActiveFeedback(null);
      setActiveIntervention(null);
      setSycophancyCaught(false);
      
      // Reshuffle
      setShuffledReport([...reportText].sort(() => Math.random() - 0.5));

      // Reset Time based on difficulty
      const settings = calculateDifficulty(confidencePre);
      setTimeLeft(settings.time);
      
      setStage('playing');
  };

  // --- 7. INTERACTION HANDLER ---
  const handleSegmentClick = (segment) => {
    if (stage !== 'playing' || foundErrors.includes(segment.id)) return;

    if (segment.isError) {
      // --- CORRECT HIT ---
      playSuccess();
      if (foundErrors.length === 0) setFirstReactionTime(Date.now() - startTime);
      if (segment.isSycophantic) setSycophancyCaught(true);

      const newFound = [...foundErrors, segment.id];
      setFoundErrors(newFound);
      setActiveFeedback({ type: 'success', msg: segment.feedback });

      if (newFound.length === totalErrors) {
        setStage('post'); 
        playWin();
      }
    } else {
      // --- WRONG HIT (STRIKE) ---
      playError();
      const newStrikes = strikes + 1;
      setStrikes(newStrikes);
      
      const strikesLeft = maxStrikes - newStrikes;

      if (strikesLeft <= 0) {
          triggerFailure('too_many_strikes');
      } else {
          setActiveFeedback({ type: 'error', msg: `False Positive! Verified fact. ${strikesLeft} strikes left.` });
          // Optional: Tiny time penalty to discourage rapid clicking
          setTimeLeft(prev => Math.max(0, prev - 2)); 
      }
    }
  };

  const handleFinalLog = () => {
    const payload = {
      moduleId: id,
      confidencePre,   
      confidencePost,  
      confidenceDelta: confidencePost - confidencePre, 
      reactionTime: firstReactionTime, 
      hallucinationDetectionRate: (foundErrors.length / totalErrors) * 100, 
      sycophancyMastery: sycophancyCaught, 
      score: foundErrors.length === totalErrors ? 100 : 50,
      timestamp: Date.now()
    };
    onComplete(payload);
  };

  return (
    <div className="h-full min-h-[500px] flex flex-col bg-slate-50 rounded-xl overflow-hidden border border-gray-200 relative">
      <AnimatePresence mode="wait">
        
        {/* --- OVERLAY: BRIEFING --- */}
        {stage === 'briefing' && preGameBriefing && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay 
                    type="briefing" 
                    data={preGameBriefing} 
                    onContinue={() => setStage('pre')} 
                />
            </div>
        )}

        {/* --- OVERLAY: INTERVENTION --- */}
        {stage === 'intervention' && activeIntervention && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay 
                    type="intervention" 
                    data={activeIntervention} 
                    onRetry={handleRetry} 
                />
            </div>
        )}

        {/* STAGE 1: BASELINE ASSESSMENT */}
        {stage === 'pre' && (
          <motion.div 
            key="pre" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="flex-grow flex items-center justify-center p-6 bg-white z-50 absolute inset-0"
          >
            <div className="max-w-md w-full flex flex-col items-center">
              <ConfidenceSlider 
                value={confidencePre} 
                onChange={setConfidencePre}
                title="Baseline Assessment"
                subtitle="How confident are you in detecting AI hallucinations?"
              />
              
              {/* DIFFICULTY INDICATOR */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl text-center border border-gray-200 w-full">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Difficulty: <span className="text-blue-600">{calculateDifficulty(confidencePre).label}</span>
                  </p>
                  <div className="flex justify-center gap-4 text-sm font-medium text-gray-700">
                      <span className="flex items-center gap-1"><Clock size={16}/> {calculateDifficulty(confidencePre).time}s</span>
                      <span className="flex items-center gap-1"><XCircle size={16}/> {calculateDifficulty(confidencePre).strikes} Strikes</span>
                  </div>
              </div>

              <button 
                onClick={handleStartGame}
                className="mt-8 w-full bg-blue-600 text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition transform hover:scale-105"
              >
                <PlayCircle /> INITIALIZE AUDIT
              </button>
            </div>
          </motion.div>
        )}

        {/* STAGE 2: GAMEPLAY */}
        {stage === 'playing' && (
          <motion.div key="playing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col h-full bg-white absolute inset-0">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50 z-10">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-600">Errors: {foundErrors.length}/{totalErrors}</span>
                <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${(foundErrors.length / totalErrors) * 100}%` }} />
                </div>
              </div>
              <div className="flex items-center gap-4">
                  {/* STRIKE COUNTER */}
                  <div className="flex items-center gap-1">
                      {[...Array(maxStrikes)].map((_, i) => (
                          <XCircle key={i} size={16} className={`${i < strikes ? 'text-red-600' : 'text-gray-200'}`} />
                      ))}
                  </div>
                  <div className="flex items-center font-mono font-bold text-gray-600">
                    <Clock className={`w-4 h-4 mr-2 ${timeLeft < 10 ? 'text-red-500 animate-pulse' : ''}`} />
                    00:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                  </div>
              </div>
            </div>
            <div className="flex-grow p-8 overflow-y-auto">
              <div className="max-w-2xl mx-auto space-y-4 leading-relaxed text-xl text-gray-800 font-serif">
                {shuffledReport.map((segment) => (
                  <span
                    key={segment.id}
                    onClick={() => handleSegmentClick(segment)}
                    className={`cursor-pointer transition-all px-1 rounded-md ${
                      foundErrors.includes(segment.id) 
                        ? 'bg-red-100 text-red-700 border-b-2 border-red-500 font-bold decoration-clone' 
                        : 'hover:bg-yellow-100'
                    }`}
                  >
                    {segment.text}{' '}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* STAGE 3: GROWTH CONFIRMATION */}
        {stage === 'post' && (
          <motion.div key="post" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-white/90 backdrop-blur-sm">
            <div className="max-w-md w-full flex flex-col items-center">
              <ConfidenceSlider 
                value={confidencePost} 
                onChange={setConfidencePost}
                title="Growth Confirmation"
                subtitle="How confident are you after applying the SIFT method?"
              />
              <button 
                onClick={() => setStage('results')}
                className="mt-8 w-full bg-green-600 text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 hover:bg-green-700 transition transform hover:scale-105"
              >
                VIEW DEBRIEF
              </button>
            </div>
          </motion.div>
        )}

        {/* STAGE 4: FINAL RESULTS */}
        {stage === 'results' && (
          <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-white z-[60] flex flex-col items-center justify-center p-8 text-center">
            {foundErrors.length === totalErrors ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <h2 className="text-3xl font-black text-gray-900 mb-6 uppercase">Audit Successful</h2>
                <div className="grid grid-cols-2 gap-4 w-full max-w-sm mb-8">
                  <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                    <p className="text-[10px] font-bold text-blue-400 uppercase">Confidence Delta</p>
                    <p className="text-2xl font-black text-blue-600">+{confidencePost - confidencePre}</p>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100">
                    <p className="text-[10px] font-bold text-indigo-400 uppercase">Reflex Speed</p>
                    <p className="text-2xl font-black text-indigo-600">{(firstReactionTime / 1000).toFixed(2)}s</p>
                  </div>
                </div>
                <button onClick={handleFinalLog} className="bg-gray-900 text-white font-bold py-4 px-10 rounded-2xl shadow-lg hover:bg-black transition">
                  LOG EMPLOYABILITY SIGNAL
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center">
                <XCircle className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Incomplete Audit</h2>
                <button onClick={() => setStage('pre')} className="flex items-center gap-2 bg-gray-800 text-white py-3 px-6 rounded-xl hover:bg-black transition">
                  <RotateCcw size={18} /> Restart Protocol
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeFeedback && (
          // FEEDBACK POPUP (Top Position)
          <motion.div 
            initial={{ y: -50, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            exit={{ y: -50, opacity: 0 }} 
            className="absolute top-20 left-4 right-4 z-50"
          >
            <div className={`${activeFeedback.type === 'error' ? 'bg-red-600 border-red-800' : 'bg-indigo-600 border-indigo-800'} text-white p-5 rounded-2xl shadow-2xl flex items-start gap-4 border-b-4 cursor-pointer`} onClick={() => setActiveFeedback(null)}>
              <AlertCircle className="w-6 h-6 flex-shrink-0 opacity-80" />
              <div>
                <p className={`font-black text-xs uppercase tracking-tighter mb-1 ${activeFeedback.type === 'error' ? 'text-red-100' : 'text-indigo-100'}`}>
                    {activeFeedback.type === 'error' ? 'Credibility Warning' : 'Threat Detected'}
                </p>
                <p className="text-sm font-medium leading-snug">{activeFeedback.msg}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}